import { useState, useRef, useMemo, useEffect } from "react";
import { nameInitials } from "../../lib/nameInitials";

/**
 * Textarea wrapper with @mention + #tag autocomplete.
 *
 * - Typing "@" opens a suggestion popup filtered by whatever follows
 *   the "@". Selecting inserts `@FullName ` and records the user id.
 * - Typing "#" opens a *separate* popup listing the provided `tags`
 *   (usually milestones). Selecting inserts `#TagLabel ` and records
 *   the tag key.
 * - Arrow keys navigate, Enter/Tab or click inserts, Esc closes.
 * - `onMentionsChange(userIds)` / `onTagsChange(tagKeys)` fire whenever
 *   the visible text or the internal maps change, so the parent doesn't
 *   have to re-parse the text.
 */
export default function MentionTextarea({
  value,
  onChange,
  onMentionsChange,
  onTagsChange,
  users = [],
  tags = [],
  currentUserId,
  placeholder,
  rows = 4,
  disabled = false,
  "data-testid": testId,
}) {
  const textareaRef = useRef(null);
  // `mode`: "@" for people, "#" for tags; null when popup closed.
  const [mode, setMode] = useState(null);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [mentions, setMentions] = useState({}); // name -> userId
  const [pickedTags, setPickedTags] = useState({}); // label -> key

  // Bubble user-id list up whenever the map or text changes.
  useEffect(() => {
    const ids = [];
    for (const [name, id] of Object.entries(mentions)) {
      if (value && value.includes(`@${name}`)) ids.push(id);
    }
    onMentionsChange?.(ids);
  }, [value, mentions, onMentionsChange]);

  useEffect(() => {
    const keys = [];
    for (const [label, key] of Object.entries(pickedTags)) {
      if (value && value.includes(`#${label}`)) keys.push(key);
    }
    onTagsChange?.(keys);
  }, [value, pickedTags, onTagsChange]);

  const suggestions = useMemo(() => {
    if (mode === null) return [];
    const q = query.toLowerCase();
    if (mode === "@") {
      // Prefix match on any name token / email localpart. Turkish-aware.
      const matches = (u) => {
        if (!q) return true;
        const name = (u.name || "").toLocaleLowerCase("tr");
        const email = (u.email || "").toLocaleLowerCase("tr");
        const localpart = email.split("@")[0] || "";
        if (name.startsWith(q) || localpart.startsWith(q)) return true;
        for (const part of name.split(/\s+/)) {
          if (part.startsWith(q)) return true;
        }
        return false;
      };
      return users
        .filter((u) => u.id !== currentUserId)
        .filter(matches)
        .sort((a, b) =>
          (a.name || a.email || "").toLocaleLowerCase("tr").localeCompare(
            (b.name || b.email || "").toLocaleLowerCase("tr"),
            "tr",
          ),
        );
    }
    return tags
      .filter((t) => (t.label || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [mode, users, tags, query, currentUserId]);

  const recomputeQuery = () => {
    const el = textareaRef.current;
    if (!el) return;
    const caret = el.selectionStart ?? 0;
    const upto = el.value.slice(0, caret);
    const atMatch = upto.match(/@([\wÇĞİÖŞÜçğıöşü .]{0,30})$/);
    const hashMatch = upto.match(/#([\wÇĞİÖŞÜçğıöşü ]{0,30})$/);
    let next = null;
    if (atMatch && hashMatch) {
      next =
        atMatch.index > hashMatch.index
          ? { mode: "@", q: atMatch[1] }
          : { mode: "#", q: hashMatch[1] };
    } else if (atMatch) {
      next = { mode: "@", q: atMatch[1] };
    } else if (hashMatch) {
      next = { mode: "#", q: hashMatch[1] };
    }
    if (next) {
      setMode(next.mode);
      setQuery(next.q);
      setActiveIdx(0);
    } else {
      setMode(null);
    }
  };

  const insertMention = (u) => {
    const el = textareaRef.current;
    if (!el) return;
    const caret = el.selectionStart ?? 0;
    const before = el.value.slice(0, caret);
    const after = el.value.slice(caret);
    const rewritten = before.replace(
      /@([\wÇĞİÖŞÜçğıöşü .]{0,30})$/,
      `@${u.name} `
    );
    onChange(rewritten + after);
    setMentions((prev) => ({ ...prev, [u.name]: u.id }));
    setMode(null);
    setTimeout(() => {
      const newCaret = rewritten.length;
      el.focus();
      el.setSelectionRange(newCaret, newCaret);
    }, 0);
  };

  const insertTag = (t) => {
    const el = textareaRef.current;
    if (!el) return;
    const caret = el.selectionStart ?? 0;
    const before = el.value.slice(0, caret);
    const after = el.value.slice(caret);
    const rewritten = before.replace(
      /#([\wÇĞİÖŞÜçğıöşü ]{0,30})$/,
      `#${t.label} `
    );
    onChange(rewritten + after);
    setPickedTags((prev) => ({ ...prev, [t.label]: t.key }));
    setMode(null);
    setTimeout(() => {
      const newCaret = rewritten.length;
      el.focus();
      el.setSelectionRange(newCaret, newCaret);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (mode === null || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const pick = suggestions[activeIdx];
      if (mode === "@") insertMention(pick);
      else insertTag(pick);
    } else if (e.key === "Escape") {
      setMode(null);
    }
  };

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setTimeout(recomputeQuery, 0);
        }}
        onKeyDown={handleKeyDown}
        onKeyUp={recomputeQuery}
        onClick={recomputeQuery}
        onBlur={() => setTimeout(() => setMode(null), 150)}
        rows={rows}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      />
      {mode !== null && suggestions.length > 0 && (
        <div
          data-testid={mode === "#" ? "tag-popup" : "mention-popup"}
          className="absolute bottom-full left-0 z-50 mb-1 w-64 max-h-64 overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-lg flex flex-col"
        >
          <div
            className={`border-b border-zinc-100 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider ${
              mode === "#" ? "bg-amber-50 text-amber-800" : "bg-zinc-50 text-zinc-500"
            }`}
          >
            {mode === "#" ? "Kilometre taşı · ↑↓ + Enter" : "Kişi seç · ↑↓ + Enter"}
          </div>
          <ul className="overflow-y-auto">
            {suggestions.map((item, idx) => (
              <li key={mode === "@" ? item.id : item.key}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (mode === "@") insertMention(item);
                    else insertTag(item);
                  }}
                  data-testid={
                    mode === "#"
                      ? `tag-option-${item.key}`
                      : `mention-option-${item.id}`
                  }
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                    idx === activeIdx
                      ? mode === "#"
                        ? "bg-amber-50 text-amber-900"
                        : "bg-violet-50 text-violet-900"
                      : "hover:bg-zinc-50"
                  }`}
                >
                  {mode === "@" ? (
                    <>
                      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-600 text-[10px] font-semibold text-white">
                        {nameInitials(item.name)}
                      </span>
                      <span className="truncate">{item.name}</span>
                      {item.email && (
                        <span className="ml-auto truncate text-[10px] text-zinc-400">
                          {item.email}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-amber-500 text-[10px] font-bold text-white">
                        #
                      </span>
                      <span className="truncate">{item.label}</span>
                      {item.assignees_count > 0 && (
                        <span className="ml-auto rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                          {item.assignees_count} kişi
                        </span>
                      )}
                    </>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Render text with `@FullName` + `#TagLabel` tokens visually chipped.
 */
export function MentionRichText({ text, mentionNames = [], tagLabels = [] }) {
  if (!text) return null;
  const names = [...mentionNames].sort((a, b) => b.length - a.length);
  const tags = [...tagLabels].sort((a, b) => b.length - a.length);
  const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const patterns = [];
  if (names.length > 0) patterns.push(`(?<mention>@(?:${names.map(escape).join("|")}))`);
  if (tags.length > 0) patterns.push(`(?<tag>#(?:${tags.map(escape).join("|")}))`);
  if (patterns.length === 0) return <>{text}</>;
  const pattern = new RegExp(patterns.join("|"), "g");

  const parts = [];
  let lastIndex = 0;
  let m;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    if (m.groups?.mention) {
      parts.push(
        <span
          key={`m-${m.index}`}
          className="mx-0.5 inline-flex items-center rounded-md bg-violet-100 px-1.5 py-0.5 text-[12px] font-medium text-violet-800"
        >
          {m.groups.mention}
        </span>
      );
    } else if (m.groups?.tag) {
      parts.push(
        <span
          key={`t-${m.index}`}
          className="mx-0.5 inline-flex items-center rounded-md bg-amber-100 px-1.5 py-0.5 text-[12px] font-medium text-amber-800"
        >
          {m.groups.tag}
        </span>
      );
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <>{parts}</>;
}
